import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import api from '../../api/config';

// 类型定义
export interface User {
  id: number;
  username: string;
  role: 'USER' | 'ADMIN';
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
};

// 异步操作：登录
export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post('/auth/login', { username, password });
      
      // 检查响应数据
      if (!response.data.success) {
        return rejectWithValue(response.data.message || '登录失败');
      }
      
      // 存储token到localStorage
      if (response.data.data && response.data.data.token) {
        localStorage.setItem('auth_token', response.data.data.token);
        return response.data.data.user;
      } else {
        return rejectWithValue('登录响应格式错误');
      }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || '登录失败');
    }
  }
);

// 异步操作：验证token
export const validateToken = createAsyncThunk(
  'auth/validateToken',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return rejectWithValue('未找到认证令牌');
      
      // 使用api实例，它的拦截器会自动添加token
      const response = await api.get('/auth/me');
      
      // 检查响应格式
      if (!response.data.success) {
        localStorage.removeItem('auth_token');
        return rejectWithValue(response.data.message || '验证失败');
      }
      
      return response.data.data;
    } catch (error: any) {
      // 清除无效的token
      localStorage.removeItem('auth_token');
      return rejectWithValue(error.response?.data?.message || '认证令牌无效');
    }
  }
);

// 创建slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // 登出
    logout: (state) => {
      localStorage.removeItem('auth_token');
      state.user = null;
    },
    // 清除错误
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // 登录
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
    
    // 验证token
    builder
      .addCase(validateToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(validateToken.fulfilled, (state, action: PayloadAction<User>) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(validateToken.rejected, (state) => {
        state.loading = false;
        state.user = null;
      });
  }
});

// 导出actions
export const { logout, clearError } = authSlice.actions;

// 导出reducer
export default authSlice.reducer; 